import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import withRouter from '../../hoc/withRouter';
import withConfig from '../../hoc/withConfig';
import { debounce } from '../../utils/debounceUtils';
import {
  PubSubEvents,
  BookingFormOptions,
  GaAnalyticEvents,
  Routes,
} from '../../constants';
import Events from '../../events';
import {
  parseQueryStrings,
  stringifyQueryStrings,
} from '../../utils/urlParamUtils';
import { trackEvent } from '../../utils/analyticUtils';
import { getUserAddressByCoords } from '../../utils/geocodeUtils';
import { pluckCityState } from '../../utils/searchUtils';
import { generateRoute } from '../../utils/routeUtils';

class YandexMap2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      markers: [],
      showUserLocationOnMap: false,
    };
    this._map = null;
    this._mapRef = createRef();
    this._onSafeMapDrag = debounce(() => this._onMapDragEnd(), 250);
    this._searchControl = null;
    this._markers = null;
  }

  componentDidMount() {
    const { isDetailsPage } = this.props;

    if (!window.ymaps) {
      throw new Error('Yandex Maps must be initialized first.');
    }

    Events.on(PubSubEvents.practiceSelected, this._onSelectMarker);
    
    window.ymaps.ready(() => {
      if (isDetailsPage) {
        this._initYandexMapOnDetailsPage();
      } else {
        this._initYandexMap();
      }
    });
  }

  componentWillUnmount() {
    this._clearPracticeMarkers();
    Events.removeListener(PubSubEvents.practiceSelected, this._onSelectMarker);
    if (this._map) {
      this._map.destroy();
    }
  }

  componentDidUpdate(prevProps) {
    const { showUserLocationOnMap, markers } = this.state;
    const { practices } = this.props;

    const practiceArrayCompare = (practices1, practices2) => {
      let isEqual = true;
      for (let index = 0; index < practices1?.length; ++index) {
        const practice = practices1[index];
        if (!practices2?.some((p) => practice.practiceId === p.practiceId)) {
          isEqual = false;
          break;
        }
      }
      return isEqual;
    };

    const isSameAddress = () => {
      const { search, hash } = this.props.location;
      const queryStringValues = parseQueryStrings(search, hash);
      const { search: prevSearch, hash: prevHash } = prevProps.location;
      const prevQueryStringValues = parseQueryStrings(prevSearch, prevHash);
      return queryStringValues.address === prevQueryStringValues.address;
    };

    const prevPracticesNotSameWithCurrent = !Array.isEqual(
      prevProps.practices,
      practices,
      practiceArrayCompare
    );

    const currPracticesNotSameWithPrevious = !Array.isEqual(
      practices,
      prevProps.practices,
      practiceArrayCompare
    );

    const shouldRecreateMarkers =
      !isSameAddress() &&
      !showUserLocationOnMap &&
      !prevPracticesNotSameWithCurrent &&
      !currPracticesNotSameWithPrevious &&
      markers.length > practices.length;

    if (
      shouldRecreateMarkers ||
      prevPracticesNotSameWithCurrent ||
      currPracticesNotSameWithPrevious
    ) {
      const { search, hash } = this.props.location;
      const queryStringValues = parseQueryStrings(search, hash);
      this._currAddress = queryStringValues.address;
      this._clearPracticeMarkers();
      this._createPracticeMarkers();
    }
  }

  _initYandexMap = () => {
    const { searchLocationCoords } = this.props;
    const { lat, lng } = searchLocationCoords;

    this._map = new window.ymaps.Map('yandex-map', {
      center: [lat, lng],
      zoom: 8,
      controls: []
    });
    this._markers = new window.ymaps.GeoObjectCollection();

    this._createPracticeMarkers();
    this._createSearchAreaControl();
    this._map.events.add('drag', this._onSafeMapDrag);
  };

  _initYandexMapOnDetailsPage = () => {
    const { practice } = this.props;
    const { position } = practice;
    const { lat, lng } = position;

    this._map = new window.ymaps.Map('yandex-map', {
      center: [lat, lng],
      zoom: 16,
      controls: ['zoomControl']
    });

    const marker = new window.ymaps.Placemark(
      [lat, lng],
      {
        balloonContent: this._createInfoWindowContent(practice)
      },
      {
        preset: 'islands#blueIcon'
      }
    );
    
    this._map.geoObjects.add(marker);
  };

  _createSearchAreaControl = () => {
    const { t } = this.props;
    
    const searchButton = new window.ymaps.control.Button({
      data: {
        content: t('text_search_this_area'),
      },
      options: {
        selectOnClick: false,
        maxWidth: 150
      }
    });

    searchButton.events.add('click', this._onSearchAreaClick);
    this._map.controls.add(searchButton);
    this._searchControl = searchButton;
  };

  _onSearchAreaClick = async () => {
    if (this.state.showUserLocationOnMap) {
      this.setState({ showUserLocationOnMap: false });
    }

    try {
      trackEvent(GaAnalyticEvents.map.searchThisAreaClick(this._currAddress));

      const mapCenter = this._map.getCenter();
      const coords = {
        latitude: mapCenter[0],
        longitude: mapCenter[1]
      };

      const { components } = await getUserAddressByCoords(coords);
      const shortenedAddress = pluckCityState(components);

      const { isFirstLoad, config, navigate, location } = this.props;
      const { search, hash } = location;
      const queryStringValues = parseQueryStrings(search, hash);

      queryStringValues.address = shortenedAddress;
      queryStringValues.lat = coords.latitude;
      queryStringValues.long = coords.longitude;
      queryStringValues.currentPage = 1;
      queryStringValues.isFirstLoad = isFirstLoad;

      const queryStrings = stringifyQueryStrings(queryStringValues);
      const resultsRoute = generateRoute(config, Routes.results);
      const url = window.location.hash.split('#')[1]?.startsWith('/result')
        ? `?${queryStrings}`
        : `./${resultsRoute}?${queryStrings}`;
      navigate(url);
    } catch (error) {
      console.error(error);
    }
  };

  _createPracticeMarkers = () => {
    try {
      const { practices } = this.props;
      const markers = [];

      if (this._map && practices.length > 0) {
        const bounds = [];
        let marker = null;

        practices.forEach((practice) => {
          const { practiceId } = practice;
          const { lat, lng } = practice.position;
          
          marker = new window.ymaps.Placemark(
            [lat, lng],
            {
              practiceId,
              balloonContent: this._createInfoWindowContent(practice)
            },
            {
              preset: 'islands#blueIcon',
              balloonMaxWidth: 300
            }
          );

          marker.events.add('click', () => {
            this._showPracticeOnSelectMarker({ practiceId });
          });

          this._map.geoObjects.add(marker);
          bounds.push([lat, lng]);
          markers.push(marker);
          this._markers.add(marker);
        });

        this.setState({ markers });
        this._map.geoObjects.add(this._markers);
        this._map.setBounds(this._markers.getBounds());
      }
    } catch (error) {
      console.error(error);
    }
  };

  _createInfoWindowContent = (practice) => {
    const { t, config } = this.props;
    const { bookingForm, appointmentUrl } = practice;

    const bookingText = bookingForm === BookingFormOptions.salesforceForm
      ? t('text_booking_request_appointment')
      : t('text_book_now');

    const cityRegion = config.hide_city_province
      ? ''
      : `, ${practice.city} ${practice.region}`;

    return `
      <div>
        <h3>${practice.name}</h3>
        <div style="font-size: 0.7rem">
          ${practice.address1}${practice.address2 ? ` ${practice.address2}` : ''}${cityRegion}
        </div>
        <div>
          <a href="tel:${practice.phone}">${practice.phone}</a>
          ${config.show_book_now && (appointmentUrl || bookingForm) ? 
            `<span>${bookingText}</span>` : 
            ''}
        </div>
      </div>
    `;
  };

  _clearPracticeMarkers = () => {
    if (this._map) {
      this._markers.removeAll();
      this._map.geoObjects.removeAll();
    }
  };

  _showPracticeOnSelectMarker = (markerData) => {
    const { practices } = this.props;
    const practice = practices.find(
      (p) => p.practiceId === markerData.practiceId
    );

    Events.emit(PubSubEvents.practiceSelected, practice);
  };

  _onMapDragEnd = () => {
    if (this._searchControl) {
      trackEvent(GaAnalyticEvents.map.scrollMapChange(this._currAddress));
    }
  };

  _onSelectMarker = (practice) => {
    if (this._map && practice?.position) {
      const { lat, lng } = practice.position;
      this._map.panTo([lat, lng]);
    }
  };

  render() {
    return (
      <div
        className={
          this.props.isDetailsPage ? 'yandex-map--details-page' : 'yandex-map'
        }
        id="yandex-map"
        ref={this._mapRef}
      ></div>
    );
  }
}

YandexMap2.propTypes = {
  practices: PropTypes.array,
  practice: PropTypes.object,
  t: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  searchLocationCoords: PropTypes.object,
  isDetailsPage: PropTypes.bool,
  isFirstLoad: PropTypes.bool,
};

YandexMap2.defaultProps = {
  practices: [],
};

export default withConfig(withRouter(withTranslation()(YandexMap2)));
